import { z } from 'zod';

import { DateTime } from '@/utils/dates';
import {
  optionalE164PhoneSchema,
  zodPlate,
  zodPlateState,
  zodTimeStamp,
} from '@/utils/zodUtils.ts';

import type { LocationListType } from './location';
import {
  DEFAULT_MAX_VEHICLE_CHANGES_PER_MONTH,
  DEFAULT_VEHICLE_SWAP_COOLDOWN_HOURS,
  MAX_VEHICLE_CHANGES_PER_MONTH_MAX,
  VEHICLE_SWAP_COOLDOWN_HOURS_MAX,
} from './plan';
import { createPaginatedResultSchema } from './shared/paginated';
import {
  addTierCapacityPolicyIssues,
  TierCapacityPolicyFieldsSchema,
  TierOveragePolicy,
} from './tierCapacityPolicy';

export { TierOveragePolicy } from './tierCapacityPolicy';

export enum PaymentModel {
  ADMIN_PAYS = 'admin_pays',
  EVERYONE_PAYS = 'everyone_pays',
}

export enum CollectionMethod {
  CHARGE_AUTOMATICALLY = 'charge_automatically',
  SEND_INVOICE = 'send_invoice',
}

export const DEFAULT_DAYS_UNTIL_DUE = 14;
export const MAX_INVOICE_CUSTOM_FIELDS = 4;
export const MAX_INVOICE_FOOTER_LENGTH = 1000;
export const MIN_BILLING_ADDRESS_LENGTH = 5;
export const MAX_BILLING_ADDRESS_LENGTH = 500;
export const MAX_CUSTOM_FIELD_NAME_LENGTH = 40;
export const MAX_CUSTOM_FIELD_VALUE_LENGTH = 140;
export const MIN_DAYS_UNTIL_DUE = 1;
export const MAX_DAYS_UNTIL_DUE = 90;
export const DEFAULT_FIRST_INVOICE_GRACE_DAYS = 0;
export const MIN_FIRST_INVOICE_GRACE_DAYS = 0;
export const MAX_FIRST_INVOICE_GRACE_DAYS = 30;

// Per-seat platform fee override (in cents). Supersedes the location's
// billing_v2.platform_fee_cents for a single group when set. $0–$1000.
export const MIN_PLATFORM_FEE_OVERRIDE_CENTS = 0;
export const MAX_PLATFORM_FEE_OVERRIDE_CENTS = 100000;

// Per-agreement terms & conditions override. Supersedes the location-wide
// billing_v2.terms_and_conditions for a single group when set. The HTML is
// sanitized server-side before storage, so the client sends raw editor markup.
export const MAX_TERMS_OVERRIDE_HTML_CHARS = 200000;

export const InvoiceCustomFieldSchema = z.object({
  name: z.string().min(1, 'Name is required').max(MAX_CUSTOM_FIELD_NAME_LENGTH),
  value: z
    .string()
    .min(1, 'Value is required')
    .max(MAX_CUSTOM_FIELD_VALUE_LENGTH),
});

export const BillingAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country: z.string(),
  raw: z.string(),
});

export type InvoiceCustomFieldType = z.infer<typeof InvoiceCustomFieldSchema>;
export type BillingAddressType = z.infer<typeof BillingAddressSchema>;

export enum AgreementStatus {
  DRAFT = 'draft',
  PENDING_ADMIN = 'pending_admin',
  PENDING_SIGNATURES = 'pending_signatures',
  PENDING_PAYMENT = 'pending_payment',
  PAYMENT_PROCESSING = 'payment_processing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  TERMINATED = 'terminated',
  EXPIRED = 'expired',
}

export enum AdminStatus {
  INVITED = 'invited',
  ACCEPTED = 'accepted',
  REMOVED = 'removed',
}

export enum SeatStatus {
  UNASSIGNED = 'unassigned',
  INVITED = 'invited',
  PENDING = 'pending',
  PENDING_PAYMENT = 'pending_payment',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  EXPIRED = 'expired',
}

export enum SignatureStatus {
  PENDING = 'pending',
  SIGNED = 'signed',
}

export enum AgreementKind {
  INDIVIDUAL = 'individual',
  GROUP_COLLECTIVE = 'group_collective',
  GROUP_MEMBER = 'group_member',
  GROUP_INDIVIDUAL = 'group_individual',
}

export enum DiscountType {
  PERCENTAGE = 'percentage',
  FIXED_AMOUNT = 'fixed_amount',
}

export enum ParkingType {
  RESERVED = 'reserved',
  UNRESERVED = 'unreserved',
}

export enum SeatBillingMode {
  BILLED = 'billed',
  LEASE_INCLUDED = 'lease_included',
}

export enum GroupDocumentType {
  CONTRACT = 'contract',
  PARKING_ABSTRACT = 'parking_abstract',
  OTHER = 'other',
}

export const GroupDocumentUploadSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  type: z.nativeEnum(GroupDocumentType),
  filename: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int(),
  expires_at: zodTimeStamp,
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export enum DocumentScanStatus {
  NO_THREATS_FOUND = 'NO_THREATS_FOUND',
  THREATS_FOUND = 'THREATS_FOUND',
  UNSUPPORTED = 'UNSUPPORTED',
  ACCESS_DENIED = 'ACCESS_DENIED',
  FAILED = 'FAILED',
}

export const GroupAgreementDocumentSchema = z.object({
  id: z.string().uuid(),
  group_agreement_id: z.string().uuid(),
  type: z.nativeEnum(GroupDocumentType),
  filename: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int(),
  download_url: z.string().url().optional(),
  scan_status: z.nativeEnum(DocumentScanStatus).optional(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export const GroupAgreementDocumentsResponseSchema = z.object({
  documents: z.array(GroupAgreementDocumentSchema).default([]),
});

export const GroupAgreementDocumentResponseSchema = z.object({
  document: GroupAgreementDocumentSchema,
});

export const UpdateGroupDocumentTypeRequestSchema = z.object({
  type: z.nativeEnum(GroupDocumentType),
});

export const AdminSignatureSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(SignatureStatus),
  signature_version: z.string(),
  signed_at: z.string().nullable(),
});

export const GroupAdminSchema = z.object({
  id: z.string().uuid(),
  group_agreement_id: z.string().uuid(),
  auth0_user_id: z.string().nullable(),
  email: z.string().email(),
  full_name: z.string().nullable(),
  phone_number: z.string().nullable().optional(),
  role: z.string().default('admin'),
  status: z.nativeEnum(AdminStatus),
  is_payer: z.boolean().optional(),
  auth0_invitation_id: z.string().nullable(),
  invitation_sent_at: z.string().nullable(),
  invitation_expires_at: z.string().nullable(),
  signature: AdminSignatureSchema.nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const SeatVehicleSchema = z.object({
  id: z.string().uuid(),
  group_seat_id: z.string().uuid(),
  plate: z.string(),
  plate_state: z.string(),
  // Display names are still returned for read/render paths and stay safe for
  // existing UI. The *_id fields are the new source of truth used to preselect
  // the make/model dropdowns on edit forms.
  make: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  make_id: z.number().int().nullable().optional(),
  model_id: z.number().int().nullable().optional(),
  color: z.string().nullable().optional(),
  permit_id: z.string().uuid().nullable().optional(),
});

export const IndividualCancellationSchema = z.object({
  cancel_at_period_end: z.boolean(),
  pending_cancel_at: zodTimeStamp.nullable().optional(),
  current_period_end: zodTimeStamp.nullable().optional(),
});

export const PendingReservedUpgradeSchema = z.object({
  target_subagreement_id: z.string(),
  target_tier_name: z.string(),
  reserved_space_identifier: z.string(),
  expires_at: zodTimeStamp,
});

export type PendingReservedUpgradeType = z.infer<
  typeof PendingReservedUpgradeSchema
>;

export const SeatSchema = z.object({
  id: z.string(),
  group_subagreement_id: z.string().uuid(),
  status: z.nativeEnum(SeatStatus),
  assigned_auth0_id: z.string().nullable(),
  assigned_email: z.string().nullable(),
  assigned_name: z.string().nullable(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  phone_number: z.string().nullable().optional(),
  invitation_token: z.string().nullable(),
  invitation_sent_at: z.string().nullable(),
  invitation_expires_at: z.string().nullable(),
  auth0_invitation_id: z.string().nullable(),
  individual_agreement_id: z.string().nullable(),
  reserved_space_identifier: z.string().nullable().optional(),
  preferred_reserved_space_id: z.string().uuid().nullable().optional(),
  preferred_reserved_space_identifier: z.string().nullable().optional(),
  is_billed: z.boolean().default(false),
  billing_mode: z.nativeEnum(SeatBillingMode).optional(),
  lease_allocation_id: z.string().uuid().nullable().optional(),
  individual_cancellation: IndividualCancellationSchema.nullable().optional(),
  pending_reserved_upgrade: PendingReservedUpgradeSchema.nullable().optional(),
  vehicles: z.array(SeatVehicleSchema).default([]),
  vehicle_change_counter_reset_at: zodTimeStamp.nullable().optional(),
  // Number of entry-direction camera events linked to any of this seat's
  // permits within the seat's current billing window (individual agreement
  // period for everyone-pays, group billing cycle for admin-pays). Server
  // returns 0 when the seat has no activity in the window or no permits yet.
  visit_count: z.number().int().default(0),
  // Most recent captured_at across the seat's permits in the same window;
  // null when no events.
  last_seen_at: zodTimeStamp.nullish(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const SignatureSchema = z.object({
  id: z.string(),
  group_agreement_id: z.string().uuid(),
  group_admin_id: z.string().uuid(),
  status: z.nativeEnum(SignatureStatus),
  signature_version: z.string(),
  signed_at: z.string().nullable(),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// Per-status seat tally on the tier. Optional because the field is omitted when
// the endpoint does not populate it; keys default to 0 to keep `.parse()` safe.
export const SeatStatusCountsSchema = z.object({
  unassigned: z.number().int().default(0),
  invited: z.number().int().default(0),
  pending: z.number().int().default(0),
  pending_payment: z.number().int().default(0),
  active: z.number().int().default(0),
  past_due: z.number().int().default(0),
  expired: z.number().int().default(0),
});

export const SubagreementSchema = z.object({
  id: z.string().uuid(),
  group_agreement_id: z.string().uuid(),
  plan_version_id: z.string().uuid(),
  tier_name: z.string(),
  seat_count: z.number().int().min(1),
  seat_counts: SeatStatusCountsSchema.nullish(),
  lease_seats_used: z.number().int().min(0).nullable().optional(),
  parking_type: z.nativeEnum(ParkingType),
  holds_valid: z.boolean().default(true),
  lease_included_seats: z.number().int().min(0).optional(),
  lease_allocation_id: z.string().uuid().nullable().optional(),
  // Null commits capacity for every seat.
  committed_seats: z.number().int().min(0).nullish(),
  overage_policy: z
    .nativeEnum(TierOveragePolicy)
    .default(TierOveragePolicy.BLOCK),
  overage_cap: z.number().int().min(1).nullish(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

const PlanVersionSchema = z.object({
  id: z.string().uuid(),
  plan_id: z.string().uuid(),
  unit_amount_cents: z.number().int(),
  currency: z.string().default('USD'),
  billing_interval: z.string(),
  max_vehicles: z.number().int().nullable().optional(),
  max_vehicle_changes_per_month: z
    .number()
    .int()
    .min(0)
    .max(MAX_VEHICLE_CHANGES_PER_MONTH_MAX)
    .default(DEFAULT_MAX_VEHICLE_CHANGES_PER_MONTH),
  vehicle_swap_cooldown_hours: z
    .number()
    .int()
    .min(0)
    .max(VEHICLE_SWAP_COOLDOWN_HOURS_MAX)
    .default(DEFAULT_VEHICLE_SWAP_COOLDOWN_HOURS),
  schedule: z.unknown().nullable().optional(),
});

export const SubagreementWithSeatsSchema = SubagreementSchema.extend({
  seats: z.array(SeatSchema).default([]),
  plan_version: PlanVersionSchema.nullable().optional(),
});

// Present only when the agreement has its own terms override (detached from
// the location terms). Mirrors the location billing terms shape so updated_at
// arrives as a Luxon DateTime, ready for DateTimeDisplay.
export const TermsOverrideSchema = z.object({
  html: z.string(),
  updated_at: zodTimeStamp,
});

export const GroupAgreementSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  organization_name: z.string(),
  payment_model: z.nativeEnum(PaymentModel),
  status: z.nativeEnum(AgreementStatus),
  agreement_version: z.string().nullable().optional(),
  discount_type: z.nativeEnum(DiscountType).nullable(),
  discount_value: z.number().nullable(),
  negotiated_start_date: z.string().nullable().optional(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable().optional(),
  trial_end_date: z.string().nullable().optional(),
  stripe_customer_id: z.string().nullable().optional(),
  stripe_subscription_id: z.string().nullable().optional(),
  cancel_at_period_end: z.boolean().optional(),
  pending_cancel_at: zodTimeStamp.nullable().optional(),
  payment_method_collected: z.boolean().optional(),
  payment_method_type: z.string().nullable().optional(),
  payment_method_last4: z.string().nullable().optional(),
  payment_method_brand: z.string().nullable().optional(),
  collection_method: z.nativeEnum(CollectionMethod).nullable().optional(),
  days_until_due: z.number().int().nullable().optional(),
  first_invoice_grace_days: z.number().int().nullable().optional(),
  invoice_footer: z.string().nullable().optional(),
  invoice_custom_fields: z
    .array(InvoiceCustomFieldSchema)
    .nullable()
    .optional(),
  billing_address: BillingAddressSchema.nullable().optional(),
  pause_collection_resumes_at: zodTimeStamp.nullable().optional(),
  platform_fee_override_cents: z
    .number()
    .int()
    .min(MIN_PLATFORM_FEE_OVERRIDE_CENTS)
    .max(MAX_PLATFORM_FEE_OVERRIDE_CENTS)
    .nullable()
    .optional(),
  terms_override: TermsOverrideSchema.optional(),
  overage_policy: z
    .nativeEnum(TierOveragePolicy)
    .default(TierOveragePolicy.BLOCK),
  overage_cap: z.number().int().min(1).nullish(),
  subagreements: z.array(SubagreementSchema).default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// Lease Allocation schemas

export const LeaseAllocationSchema = z.object({
  id: z.string().uuid(),
  group_agreement_id: z.string().uuid(),
  name: z.string(),
  notes: z.string().nullable().optional(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export const LeaseAllocationTierUsageSchema = z.object({
  tier_id: z.string().uuid(),
  tier_name: z.string(),
  parking_type: z.union([z.nativeEnum(ParkingType), z.literal('')]),
  lease_included: z.number().int(),
  lease_used: z.number().int(),
  tier_rate_cents: z.number().int(),
  tier_rate_currency: z.string(),
});

export const LeaseAllocationWithTiersSchema = LeaseAllocationSchema.extend({
  tiers: z.array(LeaseAllocationTierUsageSchema).default([]),
});

export const LeaseAllocationListSchema = z.array(
  LeaseAllocationWithTiersSchema
);

export const CreateLeaseAllocationRequestSchema = z.object({
  name: z.string().min(1, 'Lease name is required'),
  notes: z.string().optional(),
});

export const UpdateLeaseAllocationRequestSchema = z.object({
  name: z.string().min(1, 'Lease name is required').optional(),
  notes: z.string().optional(),
  clear_notes: z.boolean().optional(),
});

export const GroupAgreementDetailSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  organization_name: z.string(),
  payment_model: z.nativeEnum(PaymentModel),
  status: z.nativeEnum(AgreementStatus),
  agreement_version: z.string().nullable().optional(),
  discount_type: z.nativeEnum(DiscountType).nullable(),
  discount_value: z.number().nullable(),
  negotiated_start_date: z.string().nullable().optional(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable().optional(),
  trial_end_date: z.string().nullable().optional(),
  stripe_customer_id: z.string().nullable().optional(),
  stripe_subscription_id: z.string().nullable().optional(),
  payer_admin_id: z.string().uuid().nullable().optional(),
  cancel_at_period_end: z.boolean().optional(),
  pending_cancel_at: zodTimeStamp.nullable().optional(),
  payment_method_collected: z.boolean().optional(),
  payment_method_type: z.string().nullable().optional(),
  payment_method_last4: z.string().nullable().optional(),
  payment_method_brand: z.string().nullable().optional(),
  collection_method: z.nativeEnum(CollectionMethod).nullable().optional(),
  days_until_due: z.number().int().nullable().optional(),
  first_invoice_grace_days: z.number().int().nullable().optional(),
  invoice_footer: z.string().nullable().optional(),
  invoice_custom_fields: z
    .array(InvoiceCustomFieldSchema)
    .nullable()
    .optional(),
  billing_address: BillingAddressSchema.nullable().optional(),
  pause_collection_resumes_at: zodTimeStamp.nullable().optional(),
  platform_fee_override_cents: z
    .number()
    .int()
    .min(MIN_PLATFORM_FEE_OVERRIDE_CENTS)
    .max(MAX_PLATFORM_FEE_OVERRIDE_CENTS)
    .nullable()
    .optional(),
  terms_override: TermsOverrideSchema.optional(),
  // Agreement-level overage billing: vehicles of every tier beyond the seats
  // the agreement is billed for. A tier's own policy only governs admission.
  overage_policy: z
    .nativeEnum(TierOveragePolicy)
    .default(TierOveragePolicy.BLOCK),
  overage_cap: z.number().int().min(1).nullish(),
  admins: z.array(GroupAdminSchema).default([]),
  subagreements: z.array(SubagreementWithSeatsSchema).default([]),
  signatures: z.array(SignatureSchema).default([]),
  lease_allocations: z.array(LeaseAllocationSchema).default([]),
  documents: z.array(GroupAgreementDocumentSchema).default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const GroupAgreementListSchema = z.array(GroupAgreementSchema);

export const VehicleSchema = z.object({
  id: z.string().uuid(),
  agreement_item_id: z.string().uuid().nullable().optional(),
  plate: z.string(),
  plate_state: z.string().nullable().optional(),
  make: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  // Active permit covering this vehicle, when one is provisioned. Resolved
  // server-side by either matching (plate, plate_state) against permits on
  // the agreement item (regular members) or via the GroupSeatVehicle→Permit
  // 1:1 edge (group-individual parkers). Absent when no active or needs-
  // approval permit is bound.
  permit_id: z.string().uuid().nullable().optional(),
  created_at: zodTimeStamp.nullable().optional(),
  updated_at: zodTimeStamp.nullable().optional(),
});

export const AgreementPlanInfoSchema = z.object({
  plan_id: z.string().uuid(),
  plan_name: z.string(),
  plan_version_id: z.string().uuid(),
  unit_amount_cents: z.number().int(),
  billing_interval: z.string(),
});

export const AgreementLocationInfoSchema = z.object({
  location_id: z.string(),
  nickname: z.string().nullable(),
});

export const AgreementSchema = z.object({
  id: z.string().uuid(),
  kind: z.nativeEnum(AgreementKind),
  status: z.nativeEnum(AgreementStatus),
  payer_email: z.string().email(),
  group_id: z.string().uuid().nullable().optional(),
  stripe_subscription_id: z.string().nullable(),
  current_period_start: z.string().nullable(),
  current_period_end: z.string().nullable(),
  plan: AgreementPlanInfoSchema.optional(),
  location: AgreementLocationInfoSchema.optional(),
  vehicles: z.array(VehicleSchema).default([]),
  created_at: z.string().datetime(),
});

export const AgreementListSchema = z.array(AgreementSchema);

export const AgreementFeeBreakdownSchema = z.object({
  platform_fee_cents: z.number().int(),
  processing_fee_cents: z.number().int(),
  tax_cents: z.number().int(),
});

export const AgreementDetailPlanSchema = z.object({
  plan_id: z.string().uuid(),
  plan_name: z.string(),
  plan_version_id: z.string().uuid(),
  unit_amount_cents: z.number().int(),
  total_amount_cents: z.number().int(),
  billing_interval: z.string(),
  fee_breakdown: AgreementFeeBreakdownSchema.nullable(),
});

export const AgreementItemSchema = z.object({
  id: z.string().uuid(),
  agreement_id: z.string().uuid(),
  plan_version_id: z.string().uuid(),
  quantity: z.number().int(),
  stripe_subscription_item_id: z.string().nullable(),
  price_change_notice_at: zodTimeStamp.nullable(),
  price_change_effective_at: zodTimeStamp.nullable(),
  price_change_old_amount_cents: z.number().int().nullable(),
  price_change_new_amount_cents: z.number().int().nullable(),
  group_subagreement_id: z.string().uuid().nullable(),
  vehicles: z.array(VehicleSchema).default([]),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export const PendingPriceChangeSchema = z.object({
  old_amount_cents: z.number().int(),
  new_amount_cents: z.number().int(),
  old_fee_breakdown: AgreementFeeBreakdownSchema,
  new_fee_breakdown: AgreementFeeBreakdownSchema,
  effective_at: zodTimeStamp,
  notice_at: zodTimeStamp,
});

export const AgreementDetailSchema = z.object({
  id: z.string().uuid(),
  kind: z.nativeEnum(AgreementKind),
  location_id: z.string(),
  payer_auth0_id: z.string().nullable(),
  payer_email: z.string(),
  payer_name: z.string().nullable(),
  status: z.nativeEnum(AgreementStatus),
  payment_method_type: z.string().nullable(),
  payment_method_last4: z.string().nullable(),
  payment_method_brand: z.string().nullable(),
  stripe_customer_id: z.string().nullable(),
  stripe_subscription_id: z.string().nullable(),
  latest_invoice_id: z.string().nullable(),
  stripe_linked_at: zodTimeStamp.nullable().optional(),
  last_stripe_error: z.string().nullable(),
  stripe_subscription_status: z.string().nullable(),
  billing_cycle_anchor: zodTimeStamp.nullable().optional(),
  current_period_start: zodTimeStamp.nullable().optional(),
  current_period_end: zodTimeStamp.nullable().optional(),
  cancel_at_period_end: z.boolean(),
  pending_cancel_at: zodTimeStamp.nullable().optional(),
  canceled_at: zodTimeStamp.nullable().optional(),
  expires_at: zodTimeStamp.nullable().optional(),
  terms_accepted_at: zodTimeStamp.nullable().optional(),
  terms_version: z.string().nullable(),
  group_agreement_id: z.string().uuid().nullable(),
  address_verification_id: z.string().nullable(),
  last_late_fee_stripe_invoice_id: z.string().nullable(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
  plan: AgreementDetailPlanSchema,
  location: AgreementLocationInfoSchema,
  items: z.array(AgreementItemSchema).default([]),
  vehicles: z.array(VehicleSchema).default([]),
  vehicle_change_counter_reset_at: zodTimeStamp.nullable().optional(),
  pending_price_change: PendingPriceChangeSchema.nullable().optional(),
});

export const CreateLeaseAllocationRefSchema = z.object({
  ref: z.string().min(1),
  name: z.string().min(1, 'Lease name is required'),
  notes: z.string().optional(),
});

export const CreateTierSchema = z
  .object({
    plan_version_id: z.string().uuid({ message: 'Plan version is required' }),
    tier_name: z.string().min(1, 'Tier name is required'),
    seat_count: z.coerce.number().int().min(1, 'Seat count must be at least 1'),
    reserved_space_ids: z.array(z.string().uuid()).default([]),
    lease_included_seats: z.coerce.number().int().min(0).optional(),
    lease_allocation_id: z.string().uuid().nullable().optional(),
    lease_allocation_ref: z.string().nullable().optional(),
  })
  .merge(TierCapacityPolicyFieldsSchema)
  .superRefine((data, ctx) => {
    addTierCapacityPolicyIssues(
      {
        seatCount: data.seat_count,
        committedSeats: data.committed_seats,
        overagePolicy: data.overage_policy,
        overageCap: data.overage_cap,
      },
      ctx
    );
    if (data.lease_included_seats && data.lease_included_seats > 0) {
      if (!data.lease_allocation_id && !data.lease_allocation_ref) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Lease allocation is required when lease seats are set',
          path: ['lease_allocation_ref'],
        });
      }
      if (data.lease_included_seats > Number(data.seat_count)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Lease seats cannot exceed total seats',
          path: ['lease_included_seats'],
        });
      }
    }
  });

export const CreateAdminSchema = z.object({
  email: z.string().email('Valid email is required'),
  full_name: z.string().max(255).nullable().optional(),
  phone_number: optionalE164PhoneSchema,
  is_payer: z.boolean().optional(),
});

const CreateGroupDateSchema = z
  .object({
    start_date: zodTimeStamp,
    end_date: zodTimeStamp.nullable().optional(),
    trial_end_date: zodTimeStamp.nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.start_date) {
      const now = DateTime.now();
      const isCurrentMonth =
        data.start_date.year === now.year &&
        data.start_date.month === now.month;

      if (!isCurrentMonth && data.start_date.day !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Future months must start on the 1st',
          path: ['start_date'],
        });
      }
    }

    if (
      data.start_date &&
      data.end_date &&
      data.start_date.toMillis() > data.end_date.toMillis()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date must be before end date',
        path: ['start_date'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date must be before end date',
        path: ['end_date'],
      });
    }

    if (data.trial_end_date) {
      if (
        data.start_date &&
        data.trial_end_date.toMillis() <= data.start_date.toMillis()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Trial end date must be after start date',
          path: ['trial_end_date'],
        });
      }
      if (
        data.end_date &&
        data.trial_end_date.toMillis() >= data.end_date.toMillis()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Trial end date must be before end date',
          path: ['trial_end_date'],
        });
      }
    }
  });

const CreateGroupBaseSchema = z
  .object({
    location: z.custom<LocationListType[number]>(
      (val) => val != null && typeof val === 'object' && 'locationId' in val,
      'Location is required'
    ),
    organization_name: z.string().min(1, 'Name is required'),
    payment_model: z.nativeEnum(PaymentModel),
    admins: z
      .array(CreateAdminSchema)
      .min(1, 'At least one admin is required')
      .default([]),
    discount_type: z.nativeEnum(DiscountType).nullable().optional(),
    discount_value: z.coerce.number().min(0).nullable().optional(),
    tiers: z
      .array(CreateTierSchema)
      .min(1, 'At least one tier is required')
      .default([]),
    lease_allocations: z.array(CreateLeaseAllocationRefSchema).default([]),
    document_upload_ids: z.array(z.string().uuid()).default([]).optional(),
    collection_method: z.nativeEnum(CollectionMethod).optional(),
    days_until_due: z.coerce
      .number()
      .int()
      .min(MIN_DAYS_UNTIL_DUE)
      .max(MAX_DAYS_UNTIL_DUE)
      .optional(),
    first_invoice_grace_days: z.coerce
      .number()
      .int()
      .min(MIN_FIRST_INVOICE_GRACE_DAYS)
      .max(MAX_FIRST_INVOICE_GRACE_DAYS)
      .optional(),
    invoice_footer: z.string().max(MAX_INVOICE_FOOTER_LENGTH).optional(),
    invoice_custom_fields: z
      .array(InvoiceCustomFieldSchema)
      .max(MAX_INVOICE_CUSTOM_FIELDS)
      .optional(),
    billing_address: z
      .string()
      .min(MIN_BILLING_ADDRESS_LENGTH)
      .max(MAX_BILLING_ADDRESS_LENGTH)
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.payment_model === PaymentModel.ADMIN_PAYS) {
      const payerCount = data.admins.filter((a) => a.is_payer).length;
      if (payerCount !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Exactly one admin must be designated as the payer',
          path: ['admins'],
        });
      }
    }

    const hasAdminOnlyBillingConfig =
      data.collection_method !== undefined ||
      data.days_until_due !== undefined ||
      data.invoice_footer !== undefined ||
      (data.invoice_custom_fields && data.invoice_custom_fields.length > 0) ||
      data.billing_address !== undefined;

    if (
      data.payment_model === PaymentModel.EVERYONE_PAYS &&
      hasAdminOnlyBillingConfig
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Billing configuration is not allowed for everyone-pays groups',
        path: ['collection_method'],
      });
    }

    if (data.payment_model === PaymentModel.ADMIN_PAYS) {
      if (!data.billing_address || data.billing_address.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Billing address is required',
          path: ['billing_address'],
        });
      }

      if (
        data.collection_method === CollectionMethod.CHARGE_AUTOMATICALLY ||
        data.collection_method === undefined
      ) {
        if (data.days_until_due !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Payment term is only applicable when sending an invoice for wire / ACH credit',
            path: ['days_until_due'],
          });
        }
        if (data.invoice_footer !== undefined && data.invoice_footer !== '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Invoice footer is only applicable when sending an invoice for wire / ACH credit',
            path: ['invoice_footer'],
          });
        }
        if (
          data.invoice_custom_fields &&
          data.invoice_custom_fields.length > 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              'Custom fields are only applicable when sending an invoice for wire / ACH credit',
            path: ['invoice_custom_fields'],
          });
        }
      }
    }
  });

export const CreateGroupRequestSchema = CreateGroupBaseSchema.and(
  CreateGroupDateSchema
);

export type AdminSignatureType = z.infer<typeof AdminSignatureSchema>;
export type GroupAdminType = z.infer<typeof GroupAdminSchema>;
export type SeatVehicleType = z.infer<typeof SeatVehicleSchema>;
export type SeatType = z.infer<typeof SeatSchema>;
export type SignatureType = z.infer<typeof SignatureSchema>;
export type SeatStatusCountsType = z.infer<typeof SeatStatusCountsSchema>;
export type SubagreementType = z.infer<typeof SubagreementSchema>;
export type SubagreementWithSeatsType = z.infer<
  typeof SubagreementWithSeatsSchema
>;
export enum InvoiceStatus {
  DRAFT = 'draft',
  OPEN = 'open',
  PAID = 'paid',
  PARTIALLY_PAID = 'partially_paid',
  VOID = 'void',
  UNCOLLECTIBLE = 'uncollectible',
  REFUNDED = 'refunded',
  PARTIALLY_REFUNDED = 'partially_refunded',
}

export enum InvoiceType {
  SUBSCRIPTION = 'subscription',
  LATE_FEE = 'late_fee',
}

export enum InvoicePaymentStatus {
  OPEN = 'open',
  PAID = 'paid',
  CANCELED = 'canceled',
}

export const InvoicePaymentSchema = z.object({
  id: z.string(),
  amount_requested: z.number(),
  // Null until the payment settles: the API keeps "settled for zero" and "has
  // not settled yet" distinct, so this must never be coalesced to 0 in a total.
  amount_paid: z.number().nullable(),
  status: z.nativeEnum(InvoicePaymentStatus),
  // Stripe auto-creates one payment per invoice at finalization and keeps its
  // amount_requested synced to amount_remaining. Until it settles it is a
  // placeholder for the outstanding balance, not a payment the payer made.
  is_default: z.boolean(),
  paid_at: zodTimeStamp.nullable(),
  payment_intent_id: z.string().nullable(),
});

export const GroupInvoiceSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(InvoiceStatus),
  type: z.nativeEnum(InvoiceType).optional(),
  currency: z.string(),
  // Frozen at finalization: what the invoice was issued for. It does NOT
  // decrease as payments arrive, so it is never the balance owed.
  amount_due: z.number(),
  amount_paid: z.number(),
  // What is still owed. Use this for balances, pay actions and totals.
  amount_remaining: z.number(),
  subtotal: z.number(),
  total: z.number(),
  period_start: zodTimeStamp,
  period_end: zodTimeStamp,
  created: zodTimeStamp,
  due_date: zodTimeStamp.nullable().optional(),
  invoice_pdf: z.string().nullable().optional(),
  hosted_invoice_url: z.string().nullable().optional(),
  payer_email: z.string().nullable().optional(),
  // Always present, never null. Preview (upcoming) invoices always return [].
  payments: z.array(InvoicePaymentSchema),
});

export const GroupInvoicesResponseSchema = z.object({
  invoices: z.array(GroupInvoiceSchema),
});

export const AgreementInvoiceSchema = GroupInvoiceSchema.extend({
  description: z.string().nullable().optional(),
});

export const InvoicePaginationKeySchema = z.string();

export const GroupInvoicesPaginatedResult = createPaginatedResultSchema(
  GroupInvoiceSchema,
  InvoicePaginationKeySchema
);

export const AgreementInvoicesPaginatedResult = createPaginatedResultSchema(
  AgreementInvoiceSchema,
  InvoicePaginationKeySchema
);

export type InvoicePaymentType = z.infer<typeof InvoicePaymentSchema>;
export type GroupInvoiceType = z.infer<typeof GroupInvoiceSchema>;
export type GroupInvoicesResponseType = z.infer<
  typeof GroupInvoicesResponseSchema
>;
export type InvoicePaginationKey = z.infer<typeof InvoicePaginationKeySchema>;
export type GroupInvoicesPaginatedResultType = z.infer<
  typeof GroupInvoicesPaginatedResult
>;
export type AgreementInvoiceType = z.infer<typeof AgreementInvoiceSchema>;
export type AgreementInvoicesPaginatedResultType = z.infer<
  typeof AgreementInvoicesPaginatedResult
>;

export type GroupAgreementType = z.infer<typeof GroupAgreementSchema>;
export type GroupAgreementDetailType = z.infer<
  typeof GroupAgreementDetailSchema
>;
export type GroupAgreementListType = z.infer<typeof GroupAgreementListSchema>;
export type TermsOverrideType = z.infer<typeof TermsOverrideSchema>;
export type VehicleType = z.infer<typeof VehicleSchema>;
export type AgreementType = z.infer<typeof AgreementSchema>;
export type AgreementListType = z.infer<typeof AgreementListSchema>;
export type AgreementDetailType = z.infer<typeof AgreementDetailSchema>;
export type AgreementItemType = z.infer<typeof AgreementItemSchema>;
export type AgreementFeeBreakdownType = z.infer<
  typeof AgreementFeeBreakdownSchema
>;
export type PendingPriceChangeType = z.infer<typeof PendingPriceChangeSchema>;
export type CreateTierSchemaType = z.infer<typeof CreateTierSchema>;
export type CreateLeaseAllocationRefType = z.infer<
  typeof CreateLeaseAllocationRefSchema
>;
export type CreateAdminSchemaType = z.infer<typeof CreateAdminSchema>;
export type LeaseAllocationType = z.infer<typeof LeaseAllocationSchema>;
export type LeaseAllocationTierUsageType = z.infer<
  typeof LeaseAllocationTierUsageSchema
>;
export type LeaseAllocationWithTiersType = z.infer<
  typeof LeaseAllocationWithTiersSchema
>;
export type CreateLeaseAllocationRequestType = z.infer<
  typeof CreateLeaseAllocationRequestSchema
>;
export type UpdateLeaseAllocationRequestType = z.infer<
  typeof UpdateLeaseAllocationRequestSchema
>;
export type CreateGroupRequestSchemaType = z.infer<
  typeof CreateGroupRequestSchema
>;
export type GroupDocumentUploadType = z.infer<typeof GroupDocumentUploadSchema>;
export type GroupAgreementDocumentType = z.infer<
  typeof GroupAgreementDocumentSchema
>;
export type GroupAgreementDocumentsResponseType = z.infer<
  typeof GroupAgreementDocumentsResponseSchema
>;
export type UpdateGroupDocumentTypeRequestType = z.infer<
  typeof UpdateGroupDocumentTypeRequestSchema
>;

export const GroupAgreementPaginationKeySchema = z.object({
  created_at: z.string(),
  id: z.string().uuid(),
});

export const GroupAgreementPaginatedResult = createPaginatedResultSchema(
  GroupAgreementSchema,
  GroupAgreementPaginationKeySchema
);

export type GroupAgreementPaginationKey = z.infer<
  typeof GroupAgreementPaginationKeySchema
>;
export type GroupAgreementPaginatedResultType = z.infer<
  typeof GroupAgreementPaginatedResult
>;

export const AgreementPaginationKeySchema = z.object({
  created_at: z.string(),
  id: z.string().uuid(),
});

export const AgreementPaginatedResult = createPaginatedResultSchema(
  AgreementSchema,
  AgreementPaginationKeySchema
);

export type AgreementPaginationKey = z.infer<
  typeof AgreementPaginationKeySchema
>;
export type AgreementPaginatedResultType = z.infer<
  typeof AgreementPaginatedResult
>;

export const SeatPaginationKeySchema = z.object({
  created_at: z.string(),
  id: z.string().uuid(),
});

export const TierSeatsPaginatedResult = createPaginatedResultSchema(
  SeatSchema,
  SeatPaginationKeySchema
);

export type SeatPaginationKey = z.infer<typeof SeatPaginationKeySchema>;
export type TierSeatsPaginatedResultType = z.infer<
  typeof TierSeatsPaginatedResult
>;

export const SeatResponseSchema = z.object({
  seat: SeatSchema,
});

export const UpdateSeatPreferredSpaceRequestSchema = z.object({
  preferred_reserved_space_id: z.string().uuid('A reserved space is required'),
});

export const MoveSeatRequestSchema = z.object({
  target_subagreement_id: z.string().uuid(),
  preferred_reserved_space_id: z.string().uuid().nullable().optional(),
});

export const SwapSeatsRequestSchema = z.object({
  seat_a_id: z.string().uuid(),
  seat_b_id: z.string().uuid(),
});

export const ExchangeTierReservedSpaceRequestSchema = z.object({
  release_space_id: z.string().uuid(),
  acquire_space_id: z.string().uuid(),
});

export const SwapSeatsResponseSchema = z.object({
  seat_a: SeatSchema,
  seat_b: SeatSchema,
});

// Make/model are sent as reference IDs (not free-text names). A model_id must
// belong to the selected make_id, which the cascading UI enforces by only
// offering models of the chosen make. Color remains free text.
export const AddSeatVehicleRequestSchema = z.object({
  plate: zodPlate,
  plate_state: zodPlateState,
  make_id: z.number().int().positive().optional(),
  model_id: z.number().int().positive().optional(),
  color: z.string().max(30).optional(),
});

// PATCH sends only the changed fields. A make_id/model_id of null clears the
// stored make/model (e.g. the operator removed a previously set make).
export const UpdateSeatVehicleRequestSchema = z.object({
  make_id: z.number().int().positive().nullable().optional(),
  model_id: z.number().int().positive().nullable().optional(),
  color: z.string().max(30).optional(),
});

// The server defers and auto-detects the reduction: removed seats are credited
// automatically against the upcoming pre-billed invoice (when one exists), the
// current period is not credited (reserved capacity), and the next invoice
// bills the new count. The client therefore sends no invoice-adjustment input.
export const ReduceTierSeatsRequestSchema = z.object({
  new_seat_count: z.number().int().min(1),
  expected_current_seat_count: z.number().int().min(1),
  release_reserved_space_ids: z.array(z.string().uuid()).optional(),
  // Super-admin only. When true the downsize commits without crediting the
  // already-issued future prebill for the removed seats. The server returns
  // 403 if a non-super-admin sends it.
  skip_prebill_credit: z.boolean().optional(),
});

export const MAX_TIER_SEATS_PER_GROW = 500;

// Grow branch of the multiplexed tier-update PUT. Must not be combined with
// new_seat_count (downgrade branch) or draft fields in a single request.
export const GrowTierSeatsRequestSchema = z
  .object({
    additional_billable_seats: z
      .number()
      .int()
      .min(0)
      .max(MAX_TIER_SEATS_PER_GROW),
    additional_lease_seats: z
      .number()
      .int()
      .min(0)
      .max(MAX_TIER_SEATS_PER_GROW),
    expected_current_seat_count: z.number().int().min(0),
    add_reserved_space_ids: z.array(z.string().uuid()).optional(),
    // Omitted = true: the added seats are charged now, prorated for the days
    // left in the current period, on a separate invoice. False defers them to
    // the next cycle's invoice at full price.
    prorate: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.additional_billable_seats + data.additional_lease_seats < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one seat must be added',
        path: ['additional_billable_seats'],
      });
    }
  });

// Policy branch of the multiplexed tier-update PUT. Allowed in every live
// status and never combined with a grow or reduce in one request. Null clears
// committed_seats (commit every seat) or overage_cap.
export const UpdateTierCapacityPolicyRequestSchema = z.object({
  committed_seats: z.number().int().min(0).nullable(),
  overage_policy: z.nativeEnum(TierOveragePolicy),
  overage_cap: z.number().int().min(1).nullable(),
});

export type SeatResponseType = z.infer<typeof SeatResponseSchema>;
export type UpdateSeatPreferredSpaceRequestType = z.infer<
  typeof UpdateSeatPreferredSpaceRequestSchema
>;
export type MoveSeatRequestType = z.infer<typeof MoveSeatRequestSchema>;
export type SwapSeatsRequestType = z.infer<typeof SwapSeatsRequestSchema>;
export type ExchangeTierReservedSpaceRequestType = z.infer<
  typeof ExchangeTierReservedSpaceRequestSchema
>;
export type SwapSeatsResponseType = z.infer<typeof SwapSeatsResponseSchema>;
export type AddSeatVehicleRequestType = z.infer<
  typeof AddSeatVehicleRequestSchema
>;
export type UpdateSeatVehicleRequestType = z.infer<
  typeof UpdateSeatVehicleRequestSchema
>;
export type UpdateTierCapacityPolicyRequestType = z.infer<
  typeof UpdateTierCapacityPolicyRequestSchema
>;
export type ReduceTierSeatsRequestType = z.infer<
  typeof ReduceTierSeatsRequestSchema
>;
export type GrowTierSeatsRequestType = z.infer<
  typeof GrowTierSeatsRequestSchema
>;

export enum InvoicePreviewOutcome {
  SENT = 'sent',
  SENT_DRY_RUN = 'sent_dry_run',
  SKIPPED_ALREADY_SENT = 'skipped_already_sent',
  SKIPPED_NO_SUBSCRIPTION_ID = 'skipped_no_subscription_id',
  SKIPPED_AGREEMENT_NOT_FOUND = 'skipped_agreement_not_found',
  SKIPPED_NOT_ADMIN_PAYS = 'skipped_not_admin_pays',
  SKIPPED_STATUS_NOT_ELIGIBLE = 'skipped_status_not_eligible',
  SKIPPED_NO_PAYER_ADMIN = 'skipped_no_payer_admin',
  SKIPPED_NO_PAYER_ADMIN_EMAIL = 'skipped_no_payer_admin_email',
}

export const SendInvoicePreviewResponseSchema = z.object({
  outcome: z.nativeEnum(InvoicePreviewOutcome),
});

export type SendInvoicePreviewResponseType = z.infer<
  typeof SendInvoicePreviewResponseSchema
>;

// `payer_change` branch of PATCH /monthly-parking/groups/{id}. Re-points an
// admin_pays agreement to a different accepted admin as the billing payer.
export const ChangeGroupPayerRequestSchema = z.object({
  new_payer_admin_id: z.string().uuid(),
});

export type ChangeGroupPayerRequestType = z.infer<
  typeof ChangeGroupPayerRequestSchema
>;

export const ChangeGroupPayerResponseSchema = z.object({
  message: z.string(),
});

export type ChangeGroupPayerResponseType = z.infer<
  typeof ChangeGroupPayerResponseSchema
>;

// `collection_method` branch of PATCH /monthly-parking/groups/{id}. Switches an
// admin_pays agreement between automatic card charging and emailed invoicing
// (bidirectional). The invoice-config fields are only valid for the
// send_invoice variant; the charge_automatically variant must carry no other
// keys, so the discriminated union keeps each branch's shape distinct (the
// server returns bad_input if send_invoice fields arrive with
// charge_automatically).
const ChargeAutomaticallyBranchSchema = z.object({
  collection_method: z.literal(CollectionMethod.CHARGE_AUTOMATICALLY),
});

const SendInvoiceBranchSchema = z.object({
  collection_method: z.literal(CollectionMethod.SEND_INVOICE),
  days_until_due: z.coerce
    .number()
    .int()
    .min(MIN_DAYS_UNTIL_DUE)
    .max(MAX_DAYS_UNTIL_DUE)
    .optional(),
  first_invoice_grace_days: z.coerce
    .number()
    .int()
    .min(MIN_FIRST_INVOICE_GRACE_DAYS)
    .max(MAX_FIRST_INVOICE_GRACE_DAYS)
    .optional(),
  invoice_footer: z.string().max(MAX_INVOICE_FOOTER_LENGTH).optional(),
  invoice_custom_fields: z
    .array(InvoiceCustomFieldSchema)
    .max(MAX_INVOICE_CUSTOM_FIELDS)
    .optional(),
  billing_address: z
    .string()
    .min(MIN_BILLING_ADDRESS_LENGTH)
    .max(MAX_BILLING_ADDRESS_LENGTH)
    .optional(),
});

export const ChangeCollectionMethodRequestSchema = z.discriminatedUnion(
  'collection_method',
  [ChargeAutomaticallyBranchSchema, SendInvoiceBranchSchema]
);

export type ChangeCollectionMethodRequestType = z.infer<
  typeof ChangeCollectionMethodRequestSchema
>;

export const ChangeCollectionMethodResponseSchema = z.object({
  message: z.string(),
});

export type ChangeCollectionMethodResponseType = z.infer<
  typeof ChangeCollectionMethodResponseSchema
>;
